/**
 * Volan test-centre locator.
 *
 * Closed state shows a static map preview; the CTA opens a full locator with
 * search, country > area > centre drill-down and a Leaflet map.
 *
 * Depends on Leaflet being present (loaded on demand by this file).
 */
(function () {
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  /* Esri light-grey canvas: minimal basemap (pale land, soft water, faint
     borders, sparse labels), free with attribution, no API key. (CARTO's
     lightweight tiles now need a key.) Sage markers read clearly on it. */
  var TILE_URL =
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
  var TILE_SUBDOMAINS = '';
  var TILE_ATTR = 'Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics';

  var COUNTRY_META = {
    malaysia: { label: 'Malaysia', flag: '\u{1F1E6}\u{1F1FE}' },
    indonesia: { label: 'Indonesia', flag: '\u{1F1EE}\u{1F1E9}' },
    philippines: { label: 'Philippines', flag: '\u{1F1F5}\u{1F1ED}' }
  };

  var SE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return SE[c];
    });
  }

  /* Memoised so repeat clients (and multiple sections) share one Leaflet load. */
  var leafletPromise = null;

  function loadScript(src) {
    if (window.L) return Promise.resolve();
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.setAttribute('data-volan-leaflet', '');
      s.onload = function () {
        window.L ? resolve() : reject(new Error('leaflet missing'));
      };
      s.onerror = function () {
        leafletPromise = null;
        reject(new Error('leaflet failed to load'));
      };
      document.head.appendChild(s);
    });
    return leafletPromise;
  }

  function loadStyle(href) {
    if (document.querySelector('link[data-volan-leaflet]')) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    l.setAttribute('data-volan-leaflet', '');
    document.head.appendChild(l);
  }

  function Client(root, data) {
    this.root = root;
    this.data = (data || []).filter(function (d) {
      return d && d.name;
    });
    this.overlay = root.querySelector('[data-volan-loc-overlay]');
    this.resultsEl = root.querySelector('[data-volan-results]');
    this.searchEl = root.querySelector('[data-volan-search]');
    this.countEl = root.querySelector('[data-volan-count]');
    this.emptyEl = root.querySelector('[data-volan-empty]');
    this.resetEl = root.querySelector('[data-volan-reset]');
    this.geoEl = root.querySelector('[data-volan-geo]');
    this.geoStatusEl = root.querySelector('[data-volan-geo-status]');
    this.previewEl = root.querySelector('[data-volan-map="preview"]');
    this.fullEl = root.querySelector('[data-volan-map="full"]');
    this.openBtn = root.querySelector('[data-volan-loc-open]');
    this.query = '';
    this.openCountry = '';
    this.openArea = '';
    this.markers = {};
    this.previewMarkers = {};
    this.lastFocus = null;
    this.mapReady = false;
    this.init();
  }

  Client.prototype.init = function () {
    var self = this;
    var defaultCountry = this.root.getAttribute('data-default-country') || '';

    if (this.openBtn) {
      this.openBtn.addEventListener('click', function () {
        self.open(defaultCountry);
      });
    }
    rootClose(this);

    if (this.searchEl) {
      this.searchEl.addEventListener('input', function () {
        self.query = (self.searchEl.value || '').trim().toLowerCase();
        self.resetEl.hidden = !self.query;
        self.openCountry = '';
        self.openArea = '';
        self.render();
        if (self.mapReady) self.fitVisible();
      });
    }
    if (this.resetEl) {
      this.resetEl.addEventListener('click', function () {
        self.clearQuery();
      });
    }
    if (this.geoEl) {
      this.geoEl.addEventListener('click', function () {
        self.useMyLocation();
      });
    }

    this.render();
    this.initResults();
    this.loadMap();

    var self = this;
    window.addEventListener('resize', function () {
      if (!self.mapReady) return;
      if (self.previewMap) self.previewMap.invalidateSize();
      if (!self.overlay.hidden && self.fullMap) self.fullMap.invalidateSize();
    });
  };

  function rootClose(client) {
    var close = client.overlay && client.overlay.querySelector('[data-volan-loc-close]');
    if (!close) return;
    close.addEventListener('click', function () {
      client.close();
    });
    client.overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        client.close();
      }
      if (e.key === 'Tab') client.trapFocus(e);
    });
  }

  Client.prototype.trapFocus = function (e) {
    var nodes = this.overlay.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    var list = Array.prototype.filter.call(nodes, function (n) {
      return n.offsetParent !== null;
    });
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  Client.prototype.open = function (defaultCountry) {
    this.lastFocus = document.activeElement;
    this.overlay.hidden = false;
    document.documentElement.classList.add('volan-loc-open');
    document.body.style.overflow = 'hidden';

    var wanted = defaultCountry;
    try {
      var geo = sessionStorage.getItem('volan_geo_country');
      var map = { MY: 'malaysia', ID: 'indonesia', PH: 'philippines' };
      if (geo && map[geo]) wanted = map[geo];
    } catch (e) {}

    if (wanted && COUNTRY_META[wanted]) {
      this.openCountry = wanted;
      var first = this.areasFor(wanted)[0];
      if (first) this.openArea = first;
    }
    this.render();
    if (this.mapReady) this.fitVisible();

    var self = this;
    requestAnimationFrame(function () {
      if (self.mapReady && self.fullMap) {
        self.fullMap.invalidateSize();
        self.fitVisible();
      }
      var close = self.overlay.querySelector('[data-volan-loc-close]');
      if (close) close.focus();
    });
  };

  Client.prototype.close = function () {
    this.overlay.hidden = true;
    document.documentElement.classList.remove('volan-loc-open');
    document.body.style.overflow = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  Client.prototype.clearQuery = function () {
    this.query = '';
    if (this.searchEl) this.searchEl.value = '';
    this.resetEl.hidden = true;
    this.openCountry = '';
    this.openArea = '';
    this.render();
    if (this.mapReady) this.fitVisible();
  };

  Client.prototype.initResults = function () {
    var self = this;
    this.resultsEl.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-role="country"]');
      if (toggle) {
        var code = toggle.getAttribute('data-country');
        if (self.openCountry === code) {
          self.openCountry = '';
          self.openArea = '';
        } else {
          self.openCountry = code;
          var areas = self.areasFor(code);
          self.openArea = areas[0] || '';
        }
        self.render();
        if (self.mapReady) self.fitVisible();
        return;
      }

      var area = e.target.closest('[data-role="area"]');
      if (area) {
        var areaKey = area.getAttribute('data-area');
        var areaCode = area.getAttribute('data-country');
        if (self.openCountry === areaCode && self.openArea === areaKey) {
          self.openArea = '';
        } else {
          self.openCountry = areaCode;
          self.openArea = areaKey;
        }
        self.render();
        if (self.mapReady) self.fitVisible();
        return;
      }

      var pin = e.target.closest('[data-role="pin"]');
      if (pin) {
        self.focusLocation(pin.getAttribute('data-id'));
      }
    });
  };

  Client.prototype.countries = function () {
    var seen = [];
    var self = this;
    this.data.forEach(function (d) {
      if (self.matches(d) && seen.indexOf(d.country) === -1) seen.push(d.country);
    });
    return seen;
  };

  /* Regions within a country that contain at least one matching centre. */
  Client.prototype.areasFor = function (country) {
    var seen = [];
    var self = this;
    this.data.forEach(function (d) {
      if (d.country !== country) return;
      if (!self.matches(d)) return;
      var key = self.areaKey(d);
      if (seen.indexOf(key) === -1) seen.push(key);
    });
    return seen;
  };

  /* Tier 2 of the drill-down: region group when present, else the city/area. */
  Client.prototype.areaKey = function (d) {
    return d.group || d.area || 'Other';
  };

  Client.prototype.matches = function (d) {
    if (!this.query) return true;
    var hay = [d.name, d.area, d.group, d.kind, COUNTRY_META[d.country] && COUNTRY_META[d.country].label]
      .join(' ')
      .toLowerCase();
    return hay.indexOf(this.query) !== -1;
  };

  Client.prototype.render = function () {
    var self = this;
    var countries = this.countries();
    var total = 0;

    var html = countries
      .map(function (code) {
        var meta = COUNTRY_META[code] || { label: code, flag: '' };
        var areas = self.areasFor(code);
        // With an active search every matching country/region stays expanded.
        var isOpen = !!self.query || self.openCountry === code;
        total += areas.length;

        var body = '';
        if (isOpen) {
          body = areas
            .map(function (areaKey) {
              var items = self.data.filter(function (d) {
                return d.country === code && self.areaKey(d) === areaKey && self.matches(d);
              });
              var areaOpen = !!self.query || self.openArea === areaKey;
              return (
                '<div class="volan-loc__area' + (areaOpen ? ' is-open' : '') + '">' +
                '<button type="button" class="volan-loc__area-btn" data-role="area" data-area="' +
                esc(areaKey) + '" data-country="' + esc(code) + '" aria-expanded="' +
                (areaOpen ? 'true' : 'false') + '">' +
                '<span class="volan-loc__area-name">' + esc(areaKey) + '</span>' +
                '<span class="volan-loc__area-count">' + items.length + '</span>' +
                '<svg class="volan-loc__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>' +
                '</button>' +
                (areaOpen
                  ? '<ul class="volan-loc__items">' +
                    items.map(self.itemHtml).join('') +
                    '</ul>'
                  : '') +
                '</div>'
              );
            })
            .join('');
        }

        return (
          '<div class="volan-loc__country' + (isOpen ? ' is-open' : '') + '">' +
          '<button type="button" class="volan-loc__country-btn" data-role="country" data-country="' +
          esc(code) + '" aria-expanded="' + (isOpen ? 'true' : 'false') + '">' +
          '<span class="volan-loc__flag" aria-hidden="true">' + meta.flag + '</span>' +
          '<span class="volan-loc__country-name">' + esc(meta.label) + '</span>' +
          '<span class="volan-loc__country-count">' + self.areasFor(code).length + '</span>' +
          '<svg class="volan-loc__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>' +
          '</button>' +
          (isOpen ? '<div class="volan-loc__areas">' + body + '</div>' : '') +
          '</div>'
        );
      })
      .join('');

    this.resultsEl.innerHTML = html;
    this.emptyEl.hidden = total > 0;

    if (this.countEl) {
      this.countEl.textContent = this.query
        ? total + (total === 1 ? ' area' : ' areas')
        : countries.length + (countries.length === 1 ? ' country' : ' countries');
    }
  };

  Client.prototype.itemHtml = function (d) {
    var kind = d.kind === 'store' ? 'Store' : 'Courts';
    var hasCoords = d.lat !== null && d.lng !== null && d.lat !== undefined;
    var pin = hasCoords
      ? '<button type="button" class="volan-loc__pin" data-role="pin" data-id="' +
        esc(d.id) + '" aria-label="Show ' + esc(d.name) + ' on the map">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z"></path><circle cx="12" cy="10" r="2.5"></circle></svg>' +
        '</button>'
      : '';

    var action = d.url
      ? '<a class="button button--primary button--small" href="' +
        esc(d.url) + '" target="_blank" rel="noopener">Directions</a>'
      : '';

    return (
      '<li class="volan-loc__item">' +
      pin +
      '<div class="volan-loc__item-body">' +
      '<p class="volan-loc__item-name">' + esc(d.name) + '</p>' +
      '<p class="volan-loc__item-meta">' + esc(kind) + (d.area ? ' · ' + esc(d.area) : '') + '</p>' +
      '</div>' +
      action +
      '</li>'
    );
  };

  /* Map ------------------------------------------------------------------ */

  Client.prototype.loadMap = function () {
    var self = this;
    var hasCoords = this.data.some(function (d) {
      return d.lat !== null && d.lng !== null && d.lat !== undefined;
    });
    if (!hasCoords) return;

    if (window.L) {
      this.buildMaps();
      return;
    }
    loadStyle(LEAFLET_CSS);
    loadScript(LEAFLET_JS)
      .then(function () {
        self.buildMaps();
      })
      .catch(function () {
        /* Map is a progressive enhancement; the list still works. */
      });
  };

  Client.prototype.markerStyle = function (active) {
    return {
      radius: active ? 10 : 7,
      color: '#9DBE9D',
      weight: 0,
      fillColor: '#9DBE9D',
      fillOpacity: active ? 0.9 : 0.55
    };
  };

  Client.prototype.buildMaps = function () {
    var self = this;
    var pts = this.data.filter(function (d) {
      return d.lat !== null && d.lng !== null && d.lat !== undefined;
    });
    if (!pts.length || !window.L) return;

    if (this.previewEl) {
      this.previewMap = L.map(this.previewEl, {
        scrollWheelZoom: false,
        dragging: false,
        zoomControl: false,
        attributionControl: true
      });
      L.tileLayer(TILE_URL, { attribution: TILE_ATTR, subdomains: TILE_SUBDOMAINS, maxZoom: 19 }).addTo(
        this.previewMap
      );
      pts.forEach(function (d) {
        self.previewMarkers[d.id] = L.circleMarker([d.lat, d.lng], self.markerStyle(false)).addTo(
          self.previewMap
        );
      });
      /* Focus the preview on the visitor's country so the markers stay
         readable — otherwise KL-to-Siargao bounds flatten everything. */
      var previewPts = pts;
      var dc = this.root.getAttribute('data-default-country');
      if (dc) {
        var scoped = pts.filter(function (d) {
          return d.country === dc;
        });
        if (scoped.length) previewPts = scoped;
      }
      this.previewMap.fitBounds(
        L.latLngBounds(previewPts.map(function (d) {
          return [d.lat, d.lng];
        })),
        { padding: [40, 40], maxZoom: 11 }
      );
      this.previewMap.invalidateSize();
    }

    if (this.fullEl) {
      
        var finePointer = window.matchMedia &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        this.fullMap = L.map(this.fullEl, {
          scrollWheelZoom: !!finePointer,
          dragging: true,
          doubleClickZoom: true,
          touchZoom: true,
          keyboard: true,
          zoomControl: false
      });

      L.tileLayer(TILE_URL, { attribution: TILE_ATTR, subdomains: TILE_SUBDOMAINS, maxZoom: 19 }).addTo(
        this.fullMap
      );
      pts.forEach(function (d) {
        var m = L.circleMarker([d.lat, d.lng], self.markerStyle(false)).addTo(self.fullMap);
        m.bindPopup(self.popupHtml(d));
        m.on('click', function () {
          self.setActive(d.id);
        });
        self.markers[d.id] = m;
      });

      
var controls = this.root.querySelector('.volan-loc__mapctl');

if (controls) {
  controls.addEventListener('click', function (e) {
    var z = e.target.closest('[data-volan-zoom]');
    if (!z || !self.fullMap) return;

    e.preventDefault();
    e.stopPropagation();

    var dir = z.getAttribute('data-volan-zoom');

    if (dir === 'in') self.fullMap.zoomIn();
    else if (dir === 'out') self.fullMap.zoomOut();
  });
}


      this.mapReady = true;
    }
  };

  Client.prototype.popupHtml = function (d) {
    var kind = d.kind === 'store' ? 'Store' : 'Courts';
    var link = d.url
      ? '<a class="volan-loc__popup-link" href="' +
        esc(d.url) +
        '" target="_blank" rel="noopener">Directions</a>'
      : '';
    return (
      '<span class="volan-loc__popup-name">' + esc(d.name) + '</span>' +
      '<span class="volan-loc__popup-meta">' + esc(kind) + (d.area ? ' · ' + esc(d.area) : '') + '</span>' +
      link
    );
  };

  Client.prototype.setActive = function (id) {
    var self = this;
    Object.keys(this.markers).forEach(function (key) {
      self.markers[key].setStyle(self.markerStyle(key === id));
      if (key === id) self.markers[key].bringToFront();
    });
    Object.keys(this.previewMarkers).forEach(function (key) {
      self.previewMarkers[key].setStyle(self.markerStyle(key === id));
    });
  };

  Client.prototype.focusLocation = function (id) {
    if (!this.fullMap || !this.markers[id]) return;
    var self = this;
    this.data.forEach(function (d) {
      if (d.id === id) {
        self.fullMap.flyTo([d.lat, d.lng], Math.max(self.fullMap.getZoom(), 13), { duration: 0.6 });
      }
    });
    this.setActive(id);
    if (this.markers[id].isPopupOpen()) this.markers[id].closePopup();
    this.markers[id].openPopup();
  };

  Client.prototype.fitVisible = function () {
    if (!this.fullMap) return;
    var pts = this.data.filter(function (d) {
      return d.lat !== null && d.lng !== null && d.lat !== undefined;
    });
    if (!pts.length) return;
    if (this.openCountry) {
      pts = pts.filter(function (d) {
        return d.country === this.openCountry;
      }, this);
      if (this.openArea) {
        var area = this.openArea;
        var self = this;
        pts = pts.filter(function (d) {
          return self.areaKey(d) === area;
        });
      }
    } else {
      /* Nothing picked yet: scope to the visitor's country so markers stay
         readable instead of flattening from KL to Siargao. */
      var dc = this.root.getAttribute('data-default-country');
      if (dc) {
        var scoped = pts.filter(function (d) {
          return d.country === dc;
        });
        if (scoped.length) pts = scoped;
      }
    }
    if (!pts.length) return;
    var bounds = L.latLngBounds(
      pts.map(function (d) {
        return [d.lat, d.lng];
      })
    );
    this.fullMap.fitBounds(bounds, { padding: [56, 56], maxZoom: 14 });
  };

  Client.prototype.useMyLocation = function () {
    var self = this;
    if (!navigator.geolocation) {
      this.say('Location is not available in this browser.');
      return;
    }
    this.say('Locating you…');
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        var lat = pos.coords.latitude;
        var lng = pos.coords.longitude;
        if (self.fullMap) {
          self.fullMap.flyTo([lat, lng], 12, { duration: 0.8 });
          L.circleMarker([lat, lng], {
            radius: 8,
            color: '#1C1916',
            weight: 2,
            fillColor: '#fff',
            fillOpacity: 1
          })
            .addTo(self.fullMap)
            .bindPopup('You are here');
        }
        var nearest = self.nearest(lat, lng);
        if (nearest) {
          self.say('Nearest centre: ' + nearest.name);
          self.openCountry = nearest.country;
          self.openArea = self.areaKey(nearest);
          self.render();
          self.focusLocation(nearest.id);
        } else {
          self.say('No centres mapped near you yet.');
        }
      },
      function () {
        self.say('We could not access your location. Search for your city instead.');
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  };

  Client.prototype.nearest = function (lat, lng) {
    var best = null;
    var bestD = Infinity;
    this.data.forEach(function (d) {
      if (d.lat === null || d.lng === null || d.lat === undefined) return;
      var dx = (d.lat - lat) * Math.cos(((lat + d.lat) / 2) * (Math.PI / 180));
      var dy = d.lng - lng;
      var dist = dx * dx + dy * dy;
      if (dist < bestD) {
        bestD = dist;
        best = d;
      }
    });
    return best;
  };

  Client.prototype.say = function (msg) {
    if (this.geoStatusEl) this.geoStatusEl.textContent = msg;
  };

  function boot() {
    var roots = document.querySelectorAll('[data-volan-loc]');
    Array.prototype.forEach.call(roots, function (root) {
      var node = root.querySelector('[data-volan-loc-data]');
      if (!node) return;
      var data = [];
      try {
        data = JSON.parse(node.textContent || '[]');
      } catch (e) {
        return;
      }
      new Client(root, data);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Hero CTA on other sections points here: open the locator directly. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('a[href="#test-centres"]');
    if (!trigger) return;
    var root = document.getElementById('test-centres');
    if (!root) return;
    e.preventDefault();
    if (root.scrollIntoView) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    var open = root.querySelector('[data-volan-loc-open]');
    if (open) open.click();
  });
})();